import { useState } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Dialog,
  DialogContent,
  DialogTitle,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import { TabStrip } from '../components/TabStrip';
import { WithSideColumn } from '../components/WithSideColumn';
import { ClaudeTasksTable } from '../features/claudeTasks/ClaudeTasksTable';
import { useClaudeTasks } from '../features/claudeTasks/useClaudeTasks';
import { AddReminderForm } from '../features/clock/AddReminderForm';
import { clockApi } from '../features/clock/useClock';
import { CalendarPanel } from '../features/reminders/CalendarPanel';
import { DetailsPanel, UpcomingPanel } from '../features/reminders/ReminderPanels';
import { ReminderTable } from '../features/reminders/ReminderTable';
import { useReminders } from '../features/reminders/useReminders';
import { formatJordan } from '@serb/shared';
import { useAction } from '../lib/useAction';

const TABS = ['Calendar', 'List View', "Claude's Tasks"] as const;
const GAP = { xs: 1.5, md: 2 };

/** Reminders on a calendar and in a list. Real reminders are saved on the server; sample ones only fill the design. */
export function RemindersPage() {
  const { items, today, busy, complete, del } = useReminders();
  const tasks = useClaudeTasks();
  const [tab, setTab] = useState<(typeof TABS)[number]>('Calendar');
  const [picked, setPicked] = useState<string>();
  const [adding, setAdding] = useState(false);
  const [menu, setMenu] = useState<HTMLElement | null>(null);
  const test = useAction(clockApi.test, {
    success: (a) => `Test set for ${formatJordan(a.deliverAt)}. Lock your phone and wait a minute.`,
  });

  // something is always open: the one you picked, else the first one still to come
  const selected = items.find((r) => r.id === picked) ?? items.find((r) => !r.complete && r.date >= today) ?? items[0];

  return (
    <Stack spacing={GAP}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h4" component="h1">
            Reminders &amp; Calendar
          </Typography>
          <Typography color="text.secondary">
            Track important dates, follow-ups, and deadlines across all your shipments.
          </Typography>
        </Box>
        <ButtonGroup variant="contained" disableElevation sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
          <Button startIcon={<AddIcon />} onClick={() => setAdding(true)}>
            Add Reminder
          </Button>
          <Button size="small" aria-label="More reminder actions" onClick={(e) => setMenu(e.currentTarget)}>
            <ArrowDropDown />
          </Button>
        </ButtonGroup>
        <Menu anchorEl={menu} open={Boolean(menu)} onClose={() => setMenu(null)}>
          <MenuItem
            disabled={test.busy}
            onClick={() => {
              setMenu(null);
              test.run(undefined);
            }}
          >
            Test: remind me in 1 minute
          </MenuItem>
        </Menu>
      </Stack>

      <TabStrip tabs={TABS.map((t) => ({ value: t }))} value={tab} onChange={setTab} />

      {tab === "Claude's Tasks" ? (
        <ClaudeTasksTable items={tasks.items} />
      ) : (
        <WithSideColumn
          side={
            <>
              <UpcomingPanel items={items} today={today} selectedId={selected?.id} onSelect={setPicked} />
              <DetailsPanel r={selected} today={today} busy={busy} onComplete={complete} onDelete={del} />
            </>
          }
        >
          {tab === 'Calendar' && (
            <CalendarPanel items={items} today={today} selectedId={selected?.id} onSelect={setPicked} />
          )}
          <ReminderTable items={items} today={today} selectedId={selected?.id} onSelect={setPicked} />
        </WithSideColumn>
      )}

      <Dialog open={adding} onClose={() => setAdding(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add a reminder</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <AddReminderForm onAdded={() => setAdding(false)} />
          </Box>
        </DialogContent>
      </Dialog>
    </Stack>
  );
}
